import { usePathname } from 'expo-router';
import React, { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Platform, View } from 'react-native';
import { SceneRendererProps } from 'react-native-tab-view';

import { TabView } from 'components/tab-view';
import { ITab } from 'components/tab-view/types';
import useEffectOnce from 'hooks/use-effect-once';
import { useGetFavoriteRestaurants } from 'hooks/use-get-favorite-restaurants';
import { useGetNearbyRestaurants } from 'hooks/use-get-nearby-restaurants';
import { useGetRecentRestaurants } from 'hooks/use-get-recent-restaurants';
import { useAuthContext } from 'state/auth';
import { useCRMEventsContext } from 'state/crm-events';
import { OrderLocatorTab } from 'utils/launchdarkly/variations';

import { focusStoreLocatorFirstRestaurantResult } from '../../store-locator-focus-handoff';
import { StoreCardList } from '../store-card-list';

import { StoreCardListContainer } from './list-view.styled';

const tabPanelA11yHostStyle = {
  flex: 1,
  alignSelf: 'stretch' as const,
  width: '100%' as const,
};

const webInactiveTabPanelProps = {
  'aria-hidden': true,
  inert: true,
} as const;

const intlIdMap: { [K in OrderLocatorTab]: string } = {
  [OrderLocatorTab.NEARBY]: 'nearby',
  [OrderLocatorTab.RECENT]: 'recents',
  [OrderLocatorTab.FAVORITE]: 'favorite',
  [OrderLocatorTab.DELIVERY]: 'delivery',
};

interface IListViewProps {
  mapView__experiment?: ReactNode;
}

const ListView: React.FC<IListViewProps> = () => {
  const { formatMessage } = useIntl();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);
  const { isAuthenticated } = useAuthContext();
  const { logStoreLocatorTabView } = useCRMEventsContext();
  const pathname = usePathname();

  const availableTabs = isAuthenticated
    ? [OrderLocatorTab.NEARBY, OrderLocatorTab.FAVORITE, OrderLocatorTab.RECENT]
    : [OrderLocatorTab.NEARBY];

  const tabs = availableTabs.map(key => ({
    key,
    title: formatMessage({ id: intlIdMap[key] }),
  }));

  const favoriteTabIndex = tabs.findIndex(tab => tab.key === OrderLocatorTab.FAVORITE);

  useEffectOnce(() => {
    // Logs the first tab title when opening the Store Locator
    logStoreLocatorTabView(pathname, tabs[0].title);
  });

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      const tab = target instanceof HTMLElement ? target.closest('[role="tab"]') : null;

      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        tab instanceof HTMLElement &&
        tab.getAttribute('aria-selected') === 'true' &&
        focusStoreLocatorFirstRestaurantResult()
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  const wrapTabPanel = (routeKey: OrderLocatorTab, children: ReactNode) => {
    const sceneIndex = tabs.findIndex(t => t.key === routeKey);
    const isActive = sceneIndex === currentTabIndex;

    return (
      <View
        collapsable={false}
        style={tabPanelA11yHostStyle}
        accessible={false}
        accessibilityElementsHidden={!isActive}
        importantForAccessibility={
          Platform.OS === 'android' ? (isActive ? 'auto' : 'no-hide-descendants') : undefined
        }
        {...(Platform.OS === 'web' && !isActive ? webInactiveTabPanelProps : {})}
      >
        <StoreCardListContainer>{children}</StoreCardListContainer>
      </View>
    );
  };

  // As we need to pass additional props, we have to use a custom renderScene function instead of SceneMap
  // Do not return inline functions because  If you pass inline functions, it'll re-create the component every render, which will cause the entire route to unmount and remount every change. It's very bad for performance and will also cause any local state to be lost.
  const renderScene = ({ route }: SceneRendererProps & { route: ITab }) => {
    if (route.key === OrderLocatorTab.NEARBY) {
      return wrapTabPanel(OrderLocatorTab.NEARBY, <NearbyList />);
    }
    if (route.key === OrderLocatorTab.RECENT) {
      return wrapTabPanel(OrderLocatorTab.RECENT, <RecentsListView />);
    }

    // This makes sure favorites tab gets re-rendered every time
    // This is necessary to get the updated list in case users favorited a store on Nearby, then selected favorite tab
    if (currentTabIndex !== favoriteTabIndex) {
      return null;
    }

    return wrapTabPanel(OrderLocatorTab.FAVORITE, <FavoritesListView />);
  };

  return (
    <TabView
      activeTabIndex={currentTabIndex}
      handleTabIndexChange={(index: number) => {
        const tabName = tabs[index]?.title || '';

        setCurrentTabIndex(index);
        logStoreLocatorTabView(pathname, tabName);
      }}
      tabs={tabs}
      renderScene={renderScene}
      lazy
    />
  );
};

const NearbyList = () => {
  const { restaurants, loading, error } = useGetNearbyRestaurants();

  return (
    <StoreCardList
      currentTabHasError={!!error}
      isSearching={loading}
      restaurants={restaurants}
      tab={OrderLocatorTab.NEARBY}
    />
  );
};

const RecentsListView = () => {
  const { restaurants, loading, error } = useGetRecentRestaurants();

  return (
    <StoreCardList
      currentTabHasError={!!error}
      isSearching={loading}
      restaurants={restaurants}
      tab={OrderLocatorTab.RECENT}
    />
  );
};

const FavoritesListView = () => {
  const { restaurants, loading, error } = useGetFavoriteRestaurants();

  return (
    <StoreCardList
      currentTabHasError={!!error}
      isSearching={loading}
      restaurants={restaurants}
      tab={OrderLocatorTab.FAVORITE}
    />
  );
};

export default ListView;
